import {
  DayOfWeek,
  ScheduleAdjustmentVariant,
  CalendarStaffSelectionStrategy,
} from '@/types/schema.generated.ts'
import {
  type CalendarStatus,
  type DayOfWeek as DayOfWeekType,
  type ScheduleAdjustmentVariant as ScheduleAdjustmentVariantType,
  CalendarStaffSelectionStrategy as CalendarStaffSelectionStrategyType,
} from '@/types/schema.generated.ts'

export type TimeObject = {
  hours: number
  minutes: number
}

/**
 * ステータスを表示用の文字列に変換する
 * @param status カレンダーのステータス
 * @returns ステータスの表示文字列 ('公開' または '非公開')
 */
export const statusString = (status: CalendarStatus): string => {
  if (status === 'ACTIVE') {
    return '公開'
  } else {
    return '非公開'
  }
}

/**
 * ステータスをスタイル用のバリエーション値に変換する
 * @param status カレンダーのステータス
 * @returns スタイルバリエーション ('positive' または 'negative')
 */
export const statusVariation = (
  status: CalendarStatus
): 'positive' | 'negative' => {
  if (status === 'ACTIVE') {
    return 'positive'
  } else {
    return 'negative'
  }
}

/**
 * 時間オブジェクトをHH:MM形式の文字列に変換する
 * @param time 時間オブジェクト
 * @returns HH:MM形式の時間文字列
 */
export const formatTime = (time: TimeObject): string => {
  return `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}`
}

export const weekdayNames = new Map<DayOfWeekType, string>([
  [DayOfWeek.Sunday, '日曜日'],
  [DayOfWeek.Monday, '月曜日'],
  [DayOfWeek.Tuesday, '火曜日'],
  [DayOfWeek.Wednesday, '水曜日'],
  [DayOfWeek.Thursday, '木曜日'],
  [DayOfWeek.Friday, '金曜日'],
  [DayOfWeek.Saturday, '土曜日'],
])

/**
 * カレンダーステータス選択肢のオプション定義
 */
export const statusOptions = [
  {
    label: '公開',
    value: 'ACTIVE',
  },
  {
    label: '非公開',
    value: 'INACTIVE',
  },
]

// 日程調整種別の定義
export const scheduleAdjustmentVariants: {
  value: ScheduleAdjustmentVariantType
  label: string
}[] = [
  { value: ScheduleAdjustmentVariant.Default, label: 'デフォルト' },
  { value: ScheduleAdjustmentVariant.AutoChatReply, label: '自動応答チャット' },
  { value: ScheduleAdjustmentVariant.ScenarioChat, label: 'シナリオチャット' },
]

// スタッフ選出戦略の定義
const staffSelectionStrategyVariants: Record<
  CalendarStaffSelectionStrategyType,
  string
> = {
  [CalendarStaffSelectionStrategy.Random]: 'ランダム',
  [CalendarStaffSelectionStrategy.Priority]: '優先度順',
} as const

export const getStaffSelectionStrategyLabel = <
  K extends CalendarStaffSelectionStrategyType,
>(
  strategy: K
): (typeof staffSelectionStrategyVariants)[K] =>
  staffSelectionStrategyVariants[strategy]
